import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { supabase, initialLocationHash } from './supabase';
import { initErrorReporter, setErrorUser } from './utils/errorReporter';
import { T, S, playSound, fireConfetti, fireMini, LoadingSpinner, CHAR_POOL, getChar, CharacterChip, makeReferralCode, decodeReferralCode, getReferralFromUrl, AUTH_STARS, DAILY_CHALLENGES, ToastProvider, showToast } from './utils/appUtils';

// Always loaded — needed immediately on first paint
import './App.css';
import NeevCoinsBar from './components/Engagement/NeevCoinsBar';
import NeevCoinsShop from './components/Engagement/NeevCoinsShop';
import DailyReward, { shouldShowDailyReward, markDailyRewardShown } from './components/Engagement/DailyReward';
import MysteryMission, { incrementMissionProgress } from './components/Engagement/MysteryMission';
import StreakChest from './components/Engagement/StreakChest';
import LuckySpin from './components/Engagement/LuckySpin';
import TreasureBoxShelf, { addBox } from './components/Engagement/TreasureBox';
import NeevBot, { triggerNeevBotReaction, NeevBotMini, NeevBotLevelBar } from './components/Engagement/NeevBot';
import NeevBotChat, { saveMemory as savePetMemory } from './components/Engagement/NeevBotChat';
import { restoreCardsFromDB } from './components/KnowledgeCards/KnowledgeCards';
import ShareCardProvider, { showShareCard } from './components/Engagement/ShareCard';

// Core screens — eager loaded (needed immediately after login)
import LandingPage from './screens/LandingPage';
import AuthScreen from './screens/AuthScreen';
import HomeScreen, { StreakMilestoneModal, StreakRescueModal, OnboardingModal, PremiumModal, ParentReportScreen, ExistingUserPetPicker } from './screens/HomeScreen';
import ScreenRouter from './ScreenRouter';
import { NeevStarLogoFull, NeevStarIcon } from './components/NeevStarLogo';

// Lazy loaded — only downloaded when the user navigates to that screen
const ScenarioScreen       = lazy(()=>import('./screens/ContentScreens').then(m=>({default:m.ScenarioScreen})));
const QuizScreen           = lazy(()=>import('./screens/ContentScreens').then(m=>({default:m.QuizScreen})));
const QuizWorld            = lazy(()=>import('./screens/QuizWorld'));
const StoriesScreen        = lazy(()=>import('./screens/ContentScreens').then(m=>({default:m.StoriesScreen})));
const ChallengesScreen     = lazy(()=>import('./screens/ContentScreens').then(m=>({default:m.ChallengesScreen})));
const ComingSoonScreen     = lazy(()=>import('./screens/ContentScreens').then(m=>({default:m.ComingSoonScreen})));
const ProfileScreen        = lazy(()=>import('./screens/ProfileNewsScreens').then(m=>({default:m.ProfileScreen})));
const NewsScreen           = lazy(()=>import('./screens/ProfileNewsScreens').then(m=>({default:m.NewsScreen})));
const OlympiadScreen       = lazy(()=>import('./screens/OlympiadScreen').then(m=>({default:m.OlympiadScreen})));
const MathsScreen          = lazy(()=>import('./screens/MathsScreen').then(m=>({default:m.MathsScreen})));
const StemScreen           = lazy(()=>import('./screens/StemScreen').then(m=>({default:m.StemScreen})));
const AITutorScreen        = lazy(()=>import('./screens/AITutorScreen').then(m=>({default:m.AITutorScreen})));
const MockTestScreen       = lazy(()=>import('./screens/MockTestScreen').then(m=>({default:m.MockTestScreen})));
const LeaderboardScreen    = lazy(()=>import('./screens/LeaderboardScreen').then(m=>({default:m.LeaderboardScreen})));
const CertificateScreen    = lazy(()=>import('./screens/CertificateScreen').then(m=>({default:m.CertificateScreen})));
const LanguageScreen       = lazy(()=>import('./screens/LanguageScreen').then(m=>({default:m.LanguageScreen})));
const GamesScreen          = lazy(()=>import('./screens/MiniGames').then(m=>({default:m.GamesScreen})));
const SpotItScreen         = lazy(()=>import('./screens/SpotItBrainHomework').then(m=>({default:m.SpotItScreen})));
const BrainBuzzScreen      = lazy(()=>import('./screens/SpotItBrainHomework').then(m=>({default:m.BrainBuzzScreen})));
const HomeworkHelper       = lazy(()=>import('./screens/SpotItBrainHomework').then(m=>({default:m.HomeworkHelper})));
const GoogleOnboarding     = lazy(()=>import('./screens/AuthModals').then(m=>({default:m.GoogleOnboarding})));
const PasswordResetScreen  = lazy(()=>import('./screens/AuthModals').then(m=>({default:m.PasswordResetScreen})));
// Error boundary must NOT be lazy (React requirement)
import { ScreenErrorBoundary } from './screens/AuthModals';
import FoundingBadge from './components/Scarcity/FoundingBadge';
import { PushPermissionPrompt, usePushNotifications } from './components/Notifications/PushNotifications';
import { postActivity } from './components/Social/FriendFeed';
const DesktopSidebar       = lazy(()=>import('./screens/AuthModals').then(m=>({default:m.DesktopSidebar})));
const TeacherDashboard     = lazy(()=>import('./screens/TeacherScreens').then(m=>({default:m.TeacherDashboard})));
const TeacherPortalScreen  = lazy(()=>import('./screens/TeacherScreens').then(m=>({default:m.TeacherPortalScreen})));
const TeacherRegisterScreen= lazy(()=>import('./screens/TeacherScreens').then(m=>({default:m.TeacherRegisterScreen})));
const SubjectPracticeScreen= lazy(()=>import('./screens/SubjectPracticeScreens').then(m=>({default:m.SubjectPracticeScreen})));
const StudentProgressScreen= lazy(()=>import('./screens/SubjectPracticeScreens').then(m=>({default:m.StudentProgressScreen})));
const MoneySchoolHome      = lazy(()=>import('./components/MoneySchool/MoneySchoolHome'));
const MathsNinja           = lazy(()=>import('./components/MathsNinja/MathsNinja'));
const BizWorldEmpire       = lazy(()=>import('./components/BizWorld/BizWorldEmpire'));
const ScienceWorldHome     = lazy(()=>import('./components/ScienceWorld/ScienceWorldHome'));
const ScienceStemWorld     = lazy(()=>import('./screens/ScienceStemWorld'));
const QuizBotScreen        = lazy(()=>import('./components/QuizBot/QuizBotScreen'));
const CityMap              = lazy(()=>import('./components/NeevVerse/CityMap'));
const MyCity               = lazy(()=>import('./components/NeevVerse/MyCity'));
const CreatorStudio        = lazy(()=>import('./screens/CreatorStudio'));
const MultiplayerQuiz      = lazy(()=>import('./screens/MultiplayerQuiz'));
const NeevPetWorld         = lazy(()=>import('./components/NeevPet/NeevPetWorld'));
import PetCompanion from './components/NeevPet/PetCompanion';
const CardCollection       = lazy(()=>import('./components/KnowledgeCards/KnowledgeCards').then(m=>({default:m.CardCollection})));
const AchievementFeedFull  = lazy(()=>import('./components/Social/AchievementFeed').then(m=>({default:m.AchievementFeedFull})));

// Data files — lazy loaded since they're large
import { BRAIN_DATA as BRAIN_DATA_GRADED } from './data/brainBuzzData';
import { FLASH_DATA, FLASH_TOPICS } from './data/flashCardData';
import { SCRAMBLE_DATA, TRUEFALSE_DATA, QUICKMATH_DATA, LANG_GRADE_DATA } from './data/gamesData';

// T, S, playSound, fireConfetti, fireMini, LoadingSpinner, CharacterChip,
// makeReferralCode, AUTH_STARS, DAILY_CHALLENGES — imported from ./utils/appUtils

const LOADING_MSGS = [
  "Waking up your pet...",
  "Polishing the stars...",
  "Charging up the XP...",
  "Brewing today's mission...",
  "Summoning Lucky...",
  "Loading your adventure...",
];

function AppLoadingScreen(){
  const [msgIdx,  setMsgIdx]  = useState(0);
  const [dots,    setDots]    = useState('');
  const petType = localStorage.getItem('ns_pet_type') || 'lumie';
  const petEmoji = petType==='cosmo'?'🤖':petType==='nova'?'🌙':'⭐';
  const petColor = petType==='cosmo'?'#00BCD4':petType==='nova'?'#9C27B0':'#FFD600';

  useEffect(()=>{
    const msgTimer = setInterval(()=>setMsgIdx(i=>(i+1)%LOADING_MSGS.length), 1800);
    const dotTimer = setInterval(()=>setDots(d=>d.length>=3?'':d+'.'), 400);
    return()=>{ clearInterval(msgTimer); clearInterval(dotTimer); };
  },[]);

  return(
    <div style={{
      minHeight:'100vh',
      background:'linear-gradient(160deg,#0A0E27 0%,#1A0533 50%,#0D1B4B 100%)',
      display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
      fontFamily:"'Nunito',sans-serif", gap:0,
    }}>
      <style>{`
        @keyframes petFloat{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-14px) scale(1.05)}}
        @keyframes glowPulse{0%,100%{opacity:0.3;transform:scale(1)}50%{opacity:0.7;transform:scale(1.2)}}
        @keyframes fadeMsg{0%{opacity:0;transform:translateY(8px)}20%,80%{opacity:1;transform:translateY(0)}100%{opacity:0}}
      `}</style>

      {/* Glow behind pet */}
      <div style={{
        width:160,height:160,borderRadius:'50%',marginBottom:-80,
        background:`radial-gradient(circle, ${petColor}44 0%, transparent 70%)`,
        animation:'glowPulse 2s ease-in-out infinite',
      }}/>

      {/* Pet floating */}
      <div style={{fontSize:90,animation:'petFloat 2.2s ease-in-out infinite',marginBottom:24,lineHeight:1}}>
        {petEmoji}
      </div>

      {/* NeevStar logo */}
      <NeevStarLogoFull width={200} dark={true}/>

      {/* Rotating loading message */}
      <p key={msgIdx} style={{
        fontSize:14,fontWeight:700,color:'rgba(255,255,255,0.6)',margin:'0 0 32px',
        animation:'fadeMsg 1.8s ease-in-out',
      }}>
        {LOADING_MSGS[msgIdx]}{dots}
      </p>

      {/* Loading bar */}
      <div style={{width:160,height:4,background:'rgba(255,255,255,0.1)',borderRadius:4,overflow:'hidden'}}>
        <div style={{
          height:'100%',borderRadius:4,
          background:`linear-gradient(90deg,${petColor},white)`,
          animation:'loadBar 2s ease-in-out infinite',
        }}/>
      </div>
      <style>{`@keyframes loadBar{0%{width:0%;margin-left:0}50%{width:80%;margin-left:0}100%{width:10%;margin-left:90%}}`}</style>
    </div>
  );
}

// ── MAIN APP ─────────────────────────────────────────────────
// Init error reporter once at module level
initErrorReporter();

export default function App(){
  const [user,setUser]=useState(null);
  const [profile,setProfile]=useState(null);
  const [xp,setXp]=useState(0);
  const [streak,setStreak]=useState(0);
  const [neevCoins,setNeevCoins]=useState(0);
  const [todayXPEarned,setTodayXPEarned]=useState(()=>{
    try{return parseInt(localStorage.getItem(`ns_today_xp_${new Date().toDateString()}`)||'0',10);}catch{return 0;}
  });
  // Refs keep callbacks stable — avoids re-rendering every child screen on XP/coin change
  const xpRef=useRef(0);
  const coinsRef=useRef(0);
  const profileRef=useRef(null);
  const screenRef=useRef('home');
  const userRef=useRef(null);
  const xpSaveTimer=useRef(null);
  const coinsSaveTimer=useRef(null);
  const [showDailyReward,setShowDailyReward]=useState(false);
  const [showLuckySpin,setShowLuckySpin]=useState(false);
  const [screen,setScreen]=useState('home');
  // Optimistic auth — if we have a cached profile, show app immediately
  const [authChecked,setAuthChecked]=useState(()=>{
    try{
      const keys=Object.keys(localStorage).filter(k=>k.startsWith('ns_profile_'));
      if(keys.length>0){const p=JSON.parse(localStorage.getItem(keys[0]));if(p?.child_name)return true;}
      // Also check for cached teacher session
      if(localStorage.getItem('ns_teacher_session'))return true;
    }catch(_){}
    return false;
  });
  const [googleNewUser,setGoogleNewUser]=useState(null);
  const [xpLoaded,setXpLoaded]=useState(false);
  const [teacher,setTeacher]=useState(null);
  const [showTeacherRegister,setShowTeacherRegister]=useState(false);
  const [showLanding,setShowLanding]=useState(true);
  const [showPasswordReset,setShowPasswordReset]=useState(false);
  // showUpgradeModal + upgradeLoading removed — premium is open-access while we build the user base
  const [completedCounts,setCompletedCounts]=useState({});
  const [homeRefreshTick,setHomeRefreshTick]=useState(0);
  const [showNeevBotChat,setShowNeevBotChat]=useState(false);
  const [showOnboarding,setShowOnboarding]=useState(false);
  const [showPetPicker,setShowPetPicker]=useState(false);
  const [showFoundingBadge,setShowFoundingBadge]=useState(false);
  const [showShop,setShowShop]=useState(false);
  const [foundingNumber,setFoundingNumber]=useState(null);
  const [streakMilestone,setStreakMilestone]=useState(null);
  const [streakRescue,setStreakRescue]=useState(null); // {brokenStreak, email}
  const [pwaPrompt,setPwaPrompt]=useState(null); // beforeinstallprompt event
  const pwaPromptRef=useRef(null);
  const [showPwaBanner,setShowPwaBanner]=useState(false);

  // Push notification permission prompt
  const { showPrompt: showPushPrompt, dismiss: dismissPushPrompt } = usePushNotifications({ user, profile });

  // Map screen names to certificate vertical keys
  const verticalKeyMap={quiz:'quiz',olympiad:'olympiad',stem:'stem',maths:'maths',entrepreneurship:'biz',money:'money',brain:'brain',language:'language',stories:'stories',challenges:'challenges',news:'news'};

  // PREMIUM_FEATURE - disabled for now, all users get full access
  // const isPremium=!!(profile?.is_premium&&new Date(profile?.premium_expiry)>new Date());
  const isPremium=true;

  /* PREMIUM_FEATURE - disabled for now
  const openRazorpay=useCallback(async()=>{
    setShowUpgradeModal(false);
    setUpgradeLoading(true);
    try{
      const res=await fetch('/api/razorpay-order',{method:'POST',headers:{'Content-Type':'application/json'}});
      const{orderId,amount,currency}=await res.json();
      const options={
        key:process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount,currency,
        name:'NeevStar',
        description:'Premium — 1 Year Unlimited Access',
        order_id:orderId,
        image:'https://neevstar.com/neevstar_favicon.png',
        prefill:{name:profile?.child_name||'',email:profile?.parent_email||''},
        theme:{color:'#0066FF'},
        handler:async(response)=>{
          const verify=await fetch('/api/razorpay-verify',{
            method:'POST',
            headers:{'Content-Type':'application/json'},
            body:JSON.stringify(response),
          });
          const result=await verify.json();
          if(result.success){
            const expiry=new Date();
            expiry.setFullYear(expiry.getFullYear()+1);
            await supabase.from('bm_child_profiles')
              .update({is_premium:true,premium_expiry:expiry.toISOString()})
              .eq('parent_email',profile?.parent_email);
            setProfile(prev=>({...prev,is_premium:true,premium_expiry:expiry.toISOString()}));
            fireConfetti();
            showToast('🎉 Welcome to NeevStar Premium! Enjoy unlimited learning!');
          }
        },
        modal:{ondismiss:()=>setUpgradeLoading(false)},
      };
      const rzp=new window.Razorpay(options);
      rzp.open();
    }catch(err){
      console.error('Payment error:',err);
      showToast('Payment failed. Please try again.','error');
    }
    setUpgradeLoading(false);
  },[profile]);
  */
  // PREMIUM_FEATURE - all users get full access while building user base
  // When re-enabling: restore openRazorpay + setShowUpgradeModal(true) here
  const handleUpgrade=useCallback(()=>{},[]);

  // Calculate real streak from login history + detect milestones
  const calculateStreak=async(email,currentStreak)=>{
    const today=new Date();
    today.setHours(0,0,0,0);
    const{data}=await supabase.from('bm_child_profiles')
      .select('last_login,streak_count').eq('parent_email',email).single();
    if(!data) return 1;
    const lastLogin=data.last_login?new Date(data.last_login):null;
    let newStreak=1;
    if(lastLogin){
      const lastDate=new Date(lastLogin);
      lastDate.setHours(0,0,0,0);
      const diffDays=Math.floor((today-lastDate)/(1000*60*60*24));
      if(diffDays===0) newStreak=data.streak_count||1;
      else if(diffDays===1) newStreak=(data.streak_count||0)+1;
      else{
        // Streak broke — save broken streak, offer Lucky rescue
        const brokenStreak = data.streak_count || 1;
        const thisMonth = new Date().toISOString().slice(0,7); // YYYY-MM
        const rescueAvailable = data.streak_rescue_used_month !== thisMonth;

        // Save broken streak to Supabase for rescue window
        await supabase.from('bm_child_profiles')
          .update({ broken_streak: brokenStreak })
          .eq('parent_email', email);

        newStreak = 1;

        // Show Lucky rescue offer if eligible (streak >= 3, rescue not used this month)
        if(brokenStreak >= 3 && rescueAvailable && diffDays <= 2){
          setTimeout(()=>setStreakRescue({ brokenStreak, email }), 2000);
        }
      }
    }
    await supabase.from('bm_child_profiles')
      .update({last_login:new Date().toISOString(),streak_count:newStreak})
      .eq('parent_email',email);
    // Check for streak milestones (show once per milestone)
    const milestones=[3,7,14,30];
    for(const m of milestones){
      const mKey=`ns_streak_milestone_${email}_${m}`;
      if(newStreak>=m&&!localStorage.getItem(mKey)){
        localStorage.setItem(mKey,'1');
        setTimeout(()=>setStreakMilestone(m),1500); // slight delay after login
        postActivity({userId:userRef.current?.id,childName:profileRef.current?.child_name,neevbotLevel:profileRef.current?.current_level,activityType:'streak',activityDetail:`is on a ${m}-day streak! 🔥`});
        break; // show only the highest unshown milestone
      }
    }
    return newStreak;
  };

  // Accept Lucky's streak rescue
  const handleStreakRescue = async () => {
    if (!streakRescue) return;
    const { brokenStreak, email } = streakRescue;
    const thisMonth = new Date().toISOString().slice(0,7);
    await supabase.from('bm_child_profiles')
      .update({
        streak_count: brokenStreak,
        streak_rescue_used_month: thisMonth,
        broken_streak: 0,
      })
      .eq('parent_email', email);
    setStreak(brokenStreak);
    setStreakRescue(null);
    playSound('levelup');
    fireConfetti();
    showToast(`🔥 ${brokenStreak}-day streak RESTORED by Lucky!`, 'success');
  };

  // Keep refs in sync with state so stable callbacks always see latest values
  xpRef.current=xp;
  coinsRef.current=neevCoins;
  profileRef.current=profile;
  screenRef.current=screen;
  // Track last non-home screen for "Jump Back In"
  if(screen!=='home'&&screen!=='profile'&&screen!=='leaderboard'&&profile?.parent_email){
    localStorage.setItem(`ns_last_screen_${profile.parent_email}`,screen);
  }
  userRef.current=user;

  // Track time spent per day (for parent dashboard)
  useEffect(()=>{
    if(!user) return;
    const startTime=Date.now();
    return()=>{
      const secsSpent=Math.round((Date.now()-startTime)/1000);
      if(secsSpent<5) return; // ignore accidental touches
      const email=profileRef.current?.parent_email||user.email;
      if(!email) return;
      const key=`ns_time_${email}_${new Date().toDateString()}`;
      const prev=parseInt(localStorage.getItem(key)||'0',10);
      localStorage.setItem(key,String(prev+secsSpent));
    };
  },[user,screen]); // re-run on screen change — logs time per screen visit

  // PWA install prompt — capture beforeinstallprompt so we can trigger it manually
  useEffect(()=>{
    const handler=(e)=>{
      e.preventDefault();
      setPwaPrompt(e);
      pwaPromptRef.current=e;
      // Don't show immediately — wait until user has earned value (handled in addXP)
    };
    window.addEventListener('beforeinstallprompt',handler);
    return()=>window.removeEventListener('beforeinstallprompt',handler);
  },[]);

  // Handle mobile back button
  useEffect(()=>{
    const handlePopState=(e)=>{
      if(screen!=='home') setScreen('home');
    };
    window.addEventListener('popstate',handlePopState);
    return()=>window.removeEventListener('popstate',handlePopState);
  },[screen]);

  // Bump tick each time user returns home so MysteryMission re-reads progress
  useEffect(()=>{
    if(screen==='home') setHomeRefreshTick(t=>t+1);
  },[screen]);

  // Register service worker on login (subscription handled by PushPermissionPrompt)
  useEffect(()=>{
    if(!user||!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.register('/sw.js').catch(()=>{});
  },[user]);

  // Push state when screen changes + scroll to top + clear confetti
  useEffect(()=>{
    if(screen==='home') window.history.pushState({screen:'home'},'',' ');
    else window.history.pushState({screen},'',' ');
    window.scrollTo(0,0);
    confetti.reset();
  },[screen]);

  // Single source of truth for all auth state.
  // onAuthStateChange covers every scenario without racing:
  //   INITIAL_SESSION — fires immediately when listener registers; replays current auth state.
  //                     This handles returning users AND the case where the Google OAuth
  //                     PKCE code exchange completed before this listener was registered.
  //   SIGNED_IN       — fires for new logins (email/password) AND when the PKCE code
  //                     exchange completes AFTER this listener was registered.
  //   SIGNED_OUT      — fires on signOut(); clears state.
  //   PASSWORD_RECOVERY — fires when a password-reset link is opened.
  // Using ONLY onAuthStateChange (no separate getSession call) eliminates the race condition
  // where getSession() returned null before the PKCE exchange finished, prematurely setting
  // authChecked=true with no user and showing the login screen.
  useEffect(()=>{
    // NOTE: exchangeCodeForSession is called AFTER onAuthStateChange is registered below,
    // so PASSWORD_RECOVERY / SIGNED_IN events are never missed.
    const loadProfile=async(u)=>{
      // Check localStorage first so returning users never see onboarding again
      const cached=localStorage.getItem('ns_profile_'+u.email);
      if(cached){
        try{
          const cp=JSON.parse(cached);
          if(cp.child_name&&cp.class_level){
            // Apply cached profile immediately so app shows without waiting for DB
            setProfile(cp);setXp(cp.xp_points||0);setNeevCoins(cp.neev_coins||0);setXpLoaded(true);
          }
        }catch(_){}
      }
      try{
        const{data:p}=await supabase.from('bm_child_profiles')
          .select('*').eq('parent_email',u.email).single();
        if(p){
          // Save to localStorage as backup for future logins
          localStorage.setItem('ns_profile_'+u.email,JSON.stringify(p));
          setProfile(p);setXp(p.xp_points||0);setNeevCoins(p.neev_coins||0);setXpLoaded(true);
          setErrorUser(u.email); // tag errors with user email
          // Restore pet from Supabase if not in localStorage (new device)
          if(p.pet_type&&!localStorage.getItem('ns_pet_type')){
            localStorage.setItem('ns_pet_type',p.pet_type);
            if(p.pet_name) localStorage.setItem('ns_pet_name',p.pet_name);
          }
          // Restore pet boosts from Supabase on new device
          if(p.pet_boosts&&!localStorage.getItem('ns_pet_boosts')){
            localStorage.setItem('ns_pet_boosts',JSON.stringify(p.pet_boosts));
          }
          // Restore quiz best scores on new device
          if(p.quiz_best_scores){
            Object.entries(p.quiz_best_scores).forEach(([cat,score])=>{
              const key=`ns_bb2_${u.email}_${cat}`;
              if(!localStorage.getItem(key)) localStorage.setItem(key,String(score));
            });
          }
          // Restore knowledge cards from Supabase on new device
          restoreCardsFromDB(u.email).catch(()=>{});
          // Existing user with no pet — show pet picker after 2s
          if(!p.pet_type&&!localStorage.getItem('ns_pet_type')&&!localStorage.getItem('ns_onboarded_'+u.email)){
            setTimeout(()=>setShowPetPicker(true),2000);
          }
          // Show Founding Explorer badge once
          if(p.is_founding_explorer&&p.founding_explorer_number&&!localStorage.getItem('ns_founding_shown_'+u.email)){
            localStorage.setItem('ns_founding_shown_'+u.email,'1');
            setFoundingNumber(p.founding_explorer_number);
            setTimeout(()=>setShowFoundingBadge(true),1500);
          }
          calculateStreak(u.email,p.streak_count||0).then(s=>{
            setStreak(s);
            // Show daily reward once per day
            if(shouldShowDailyReward(u.email)){
              markDailyRewardShown(u.email);
              setTimeout(()=>{setShowDailyReward(true);triggerNeevBotReaction('login');},1200);
            }
          });
        } else if(!cached){
          // Only show onboarding if no cached profile either
          setGoogleNewUser(u);
        }
      }catch(e){
        // DB failed but cached profile already applied — user can still use app
      }
    };

    // Safety timeout — if Supabase doesn't respond in 5s, show login screen anyway
    const authTimeout=setTimeout(()=>setAuthChecked(true),3000);

    const{data:{subscription}}=supabase.auth.onAuthStateChange(async(event,session)=>{
      clearTimeout(authTimeout);
      if(event==='PASSWORD_RECOVERY'){
        window.history.replaceState({},'','/');
        setShowPasswordReset(true);
        setAuthChecked(true);
        return;
      }

      if(event==='SIGNED_OUT'){
        setUser(null);setProfile(null);setXp(0);
        setAuthChecked(true);
        return;
      }

      // INITIAL_SESSION: replays auth state when listener registers (always fires first).
      // SIGNED_IN: fires on new login or when Google PKCE exchange completes.
      if(event==='INITIAL_SESSION'||event==='SIGNED_IN'){
        if(session?.user){
          setShowLanding(false);
          if(window.location.search.includes('code=')){
            window.history.replaceState({},'','/');
          }
          // Show app immediately — don't block on DB queries
          setUser(session.user);
          setAuthChecked(true);
          // Check teacher status in background (non-blocking)
          supabase.from('ns_teachers').select('*').eq('email',session.user.email).single()
            .then(({data:teacherData})=>{
              if(teacherData){
                localStorage.setItem('ns_teacher_session','1');
                setTeacher(teacherData);
              } else {
                localStorage.removeItem('ns_teacher_session');
                loadProfile(session.user);
              }
            }).catch(()=>{loadProfile(session.user);});
        } else {
          setAuthChecked(true);
        }
      }
    });

    // Handle password-reset and OAuth PKCE code — must come AFTER onAuthStateChange
    // so PASSWORD_RECOVERY event is caught by the listener above.
    const code=new URLSearchParams(window.location.search).get('code');
    if(code){
      supabase.auth.exchangeCodeForSession(code).catch(()=>{});
    }

    return()=>{subscription.unsubscribe();clearTimeout(authTimeout);};
  },[]);

  // Stable reference — uses refs so child screens never re-render just because XP/coins changed
  const addCoins=useCallback((amount)=>{
    if(!amount||amount<=0)return;
    playSound('coin');
    setNeevCoins(prev=>{
      const newCoins=prev+amount;
      coinsRef.current=newCoins;
      // Debounce DB write: wait 2s after last coin award before hitting Supabase
      if(coinsSaveTimer.current) clearTimeout(coinsSaveTimer.current);
      coinsSaveTimer.current=setTimeout(()=>{
        const p=profileRef.current;
        if(p) supabase.from('bm_child_profiles').update({neev_coins:coinsRef.current}).eq('parent_email',p.parent_email).then(()=>{}).catch(()=>{});
      },2000);
      return newCoins;
    });
  },[]); // eslint-disable-line react-hooks/exhaustive-deps

  const spendCoins=useCallback((amount)=>{
    if(!amount||amount<=0) return;
    setNeevCoins(prev=>{
      const newCoins=Math.max(0,prev-amount);
      coinsRef.current=newCoins;
      const p=profileRef.current;
      if(p) supabase.from('bm_child_profiles').update({neev_coins:newCoins}).eq('parent_email',p.parent_email).then(()=>{}).catch(()=>{});
      return newCoins;
    });
  },[]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stable reference — uses refs so child screens never re-render just because XP changed
  const addXP=useCallback((amount)=>{
    // Apply XP Boost if active
    const p0=profileRef.current;
    const boostKey=`ns_xp_boost_${p0?.parent_email}`;
    const boosts=parseInt(localStorage.getItem(boostKey)||'0',10);
    let finalAmount=amount;
    if(boosts>0){
      finalAmount=amount*2;
      localStorage.setItem(boostKey,String(boosts-1));
      // Visual indicator
      setTimeout(()=>showShareCard('level','2× XP Boost Active!',p0?.child_name,finalAmount),200);
    }
    setXp(prev=>{
      const newXP=prev+finalAmount;
      const oldLevel=Math.floor(prev/200)+1;
      const newLevel=Math.floor(newXP/200)+1;
      xpRef.current=newXP;
      if(newLevel>oldLevel){
        playSound('levelup');fireConfetti();triggerNeevBotReaction('levelup');
        const levelNames=['Curious Cub','Smart Starter','Market Explorer','Deal Maker','Brand Builder','Team Leader','Digital Pioneer','Impact Maker','Scale Champion','Young CEO'];
        const p=profileRef.current;
        setTimeout(()=>showShareCard('level',`Level ${newLevel} — ${levelNames[Math.min(newLevel-1,9)]}`,p?.child_name,0),800);
        postActivity({userId:userRef.current?.id,childName:p?.child_name,neevbotLevel:newLevel,activityType:'levelup',activityDetail:`reached Level ${newLevel} — ${levelNames[Math.min(newLevel-1,9)]}!`});
      } else {triggerNeevBotReaction('correct');}
      // Debounce DB write: batch rapid XP gains into one Supabase call
      if(xpSaveTimer.current) clearTimeout(xpSaveTimer.current);
      xpSaveTimer.current=setTimeout(()=>{
        const p=profileRef.current;
        const nx=xpRef.current;
        const nl=Math.floor(nx/200)+1;
        if(p) supabase.from('bm_child_profiles').update({xp_points:nx,current_level:nl,last_active_date:new Date().toISOString().split('T')[0]}).eq('parent_email',p.parent_email).then(()=>{}).catch(()=>{});
      },2000);
      return newXP;
    });
    // Award NeevCoins: approx half XP amount in coins
    addCoins(Math.max(1,Math.round(finalAmount)));
    // Common Box — every 10 correct answers
    const p=profileRef.current;
    if(p?.parent_email){
      const ansKey=`ns_ans_count_${p.parent_email}`;
      const ansCount=(parseInt(localStorage.getItem(ansKey)||'0',10))+1;
      localStorage.setItem(ansKey,String(ansCount));
      if(ansCount%10===0) addBox(p.parent_email,'common');
    }
    // Track completion per vertical (for certificates + MyCity buildings)
    const vk=verticalKeyMap[screenRef.current];
    const email=userRef.current?.email;
    const CERT_TARGETS={quiz:5,olympiad:5,maths:5,stem:5,ai:3,biz:3,money:3,news:3,stories:3,challenges:3};
    const CERT_LABELS={quiz:'Quiz Zone',olympiad:'Olympiad Prep',maths:'Maths',stem:'STEM Lab',ai:'AI School',biz:'Biz World',money:'Money School',news:'News',stories:'Stories',challenges:'Idea Lab'};
    if(vk&&email){
      setCompletedCounts(prev=>{
        const next={...prev,[vk]:(prev[vk]||0)+1};
        localStorage.setItem('ns_completions_'+email,JSON.stringify(next));
        supabase.from('bm_child_profiles').update({completed_counts:next}).eq('parent_email',email).then(()=>{}).catch(()=>{});
        if(next[vk]%1===0) addBox(email,'rare');

        // ── Certificate earned? ──────────────────────────────
        const target=CERT_TARGETS[vk];
        const label=CERT_LABELS[vk]||vk;
        if(target && next[vk]===target){
          const certKey=`ns_cert_notified_${email}_${vk}`;
          if(!localStorage.getItem(certKey)){
            localStorage.setItem(certKey,'1');
            const p=profileRef.current;
            const childName=p?.child_name||'Champion';
            const petType=localStorage.getItem('ns_pet_type')||'lumie';
            const petName=localStorage.getItem('ns_pet_name')||'Lucky';

            // Lucky reacts via PetCompanion bubble (after 1s delay)
            setTimeout(()=>{
              const msgs={
                lumie:     `A CERTIFICATE IN ${label.toUpperCase()}!! I'm crying!! I KNEW you could do this!! 🏆⭐`,
                cosmo:     `CERTIFICATE ACQUIRED — ${label.toUpperCase()} — COSMO IS EXPERIENCING MAXIMUM PRIDE!! 🤖🏆`,
                nova:      `${label} Certificate... The ancient records now bear your name, ${childName}. 🌙🏆`,
                neevo:     `A CERTIFICATE IN ${label.toUpperCase()}!! The cosmos just stopped to applaud, ${childName}!! I KNEW you were destined for this!! 🏆✨`,
                luna:      `${label.toUpperCase()} CERTIFICATE!! ${childName}, this is your masterpiece!! Frame it — it belongs on a wall!! 🎨🏆`,
                nova_char: `${label} Certificate confirmed, ${childName}. Discovery logged. This goes in the greatest achievements file. Right at the top. 🔭🏆`,
                spark:     `CERTIFICATE!! ${label.toUpperCase()}!! ${childName} I FELT THAT FROM ACROSS THE UNIVERSE — CORRECT AND CERTIFIED!! ⚡🏆`,
                glint:     `${label.toUpperCase()} CERTIFICATE!! ${childName}, this is the rarest gem of all — add it to the vault IMMEDIATELY!! 💎🏆`,
                orbi:      `${label} Certificate. ${childName}... I have orbited many worlds. I have never been prouder than right now. 🪐🏆`,
                zuki:      `CERTIFICATE!! ${label.toUpperCase()}!! ${childName} YOU DID IT AND YOU DID IT FAST — CHAMPION STATUS CONFIRMED!! 🔥🏆`,
                echo:      `${label} Certificate, ${childName}. This achievement will echo long after today. I'll make sure of it. 🌊🏆`,
                terra:     `${label} Certificate, ${childName}. You planted a seed and grew something extraordinary. The earth is proud of you. 🌳🏆`,
              };
              window.dispatchEvent(new CustomEvent('ns_pet_bubble',{detail:{text:msgs[petType]||msgs.lumie,type:'excited'}}));
            },1000);

            // Save to pet memory
            savePetMemory(email,{lastAchievement:`${label} Certificate`}).catch(()=>{});

            // Send parent push notification
            fetch('/api/send-cert-push',{
              method:'POST',
              headers:{'Content-Type':'application/json'},
              body:JSON.stringify({email,childName,label,petName,petType}),
            }).catch(()=>{});

            // Fire confetti
            setTimeout(()=>fireConfetti(),500);
          }
        }
        return next;
      });
    }
    // Mark today as active for streak reminder
    localStorage.setItem('ns_last_active',new Date().toDateString());
    // Track XP earned today for Daily Goal
    const todayKey=`ns_today_xp_${new Date().toDateString()}`;
    const prev=parseInt(localStorage.getItem(todayKey)||'0',10);
    const updated=prev+finalAmount;
    localStorage.setItem(todayKey,String(updated));
    setTodayXPEarned(updated);
    // Show PWA install banner after earning 50 XP (user has seen value, good time to suggest install)
    const newXP2=xpRef.current;
    if(newXP2>=50&&!localStorage.getItem('ns_pwa_dismissed')&&pwaPromptRef.current){
      setShowPwaBanner(true);
    }
  },[addCoins]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAuth=(u,p)=>{
    setUser(u);
    if(p){
      setProfile(p);setXp(p.xp_points||0);setNeevCoins(p.neev_coins||0);
      if(u?.email) localStorage.setItem('ns_profile_'+u.email,JSON.stringify(p));
      // Show onboarding for brand-new users (no XP yet, never seen it)
      if((p.xp_points||0)===0&&!localStorage.getItem('ns_onboarded_'+u.email)){
        setShowOnboarding(true);
      } else if(u?.email&&shouldShowDailyReward(u.email)){
        markDailyRewardShown(u.email);
        setTimeout(()=>setShowDailyReward(true),1200);
      }
    }
    setScreen('home');
  };

  const handleLogout=async()=>{
    if(user?.email) localStorage.removeItem('ns_profile_'+user.email);
    localStorage.removeItem('ns_teacher_session');
    await supabase.auth.signOut();
    setUser(null);setProfile(null);setXp(0);setCompletedCounts({});setScreen('home');
  };

  // Award daily challenge bonus XP (once per day, tracked in localStorage)
  const handleDailyChallenge=useCallback((targetScreen,bonusXP)=>{
    const todayKey=`ns_daily_${profile?.parent_email}_${new Date().toDateString()}`;
    if(!localStorage.getItem(todayKey)){
      localStorage.setItem(todayKey,'1');
      addXP(bonusXP);
      fireConfetti();
    }
    setScreen(targetScreen);
  },[profile,addXP]);

  const handleGradeChange=async(newGrade)=>{
    if(!profile) return;
    const updated={...profile,class_level:newGrade};
    setProfile(updated);
    if(user?.email) localStorage.setItem('ns_profile_'+user.email,JSON.stringify(updated));
    await supabase.from('bm_child_profiles')
      .update({class_level:newGrade})
      .eq('parent_email',profile.parent_email);
  };

  // Load completion counts — Supabase is source of truth, localStorage as fallback
  useEffect(()=>{
    if(!user?.email) return;
    supabase.from('bm_child_profiles').select('completed_counts').eq('parent_email',user.email).single()
      .then(({data})=>{
        const remote=data?.completed_counts;
        const localRaw=localStorage.getItem('ns_completions_'+user.email);
        const local=localRaw?JSON.parse(localRaw):{};
        if(remote&&typeof remote==='object'){
          // Merge: take max of each key so no progress is lost
          const merged={...local};
          Object.keys(remote).forEach(k=>{merged[k]=Math.max(merged[k]||0,remote[k]||0);});
          setCompletedCounts(merged);
          localStorage.setItem('ns_completions_'+user.email,JSON.stringify(merged));
        } else if(localRaw){
          try{setCompletedCounts(JSON.parse(localRaw));}catch(_){}
        }
      }).catch(()=>{
        const saved=localStorage.getItem('ns_completions_'+user.email);
        if(saved){try{setCompletedCounts(JSON.parse(saved));}catch(_){}}
      });
  },[user?.email]);

  if(!authChecked) return <AppLoadingScreen/>;


  // Password reset screen
  if(showPasswordReset){
    return(
      <div style={S.app}>
        <PasswordResetScreen onDone={()=>{setShowPasswordReset(false);}}/>
      </div>
    );
  }

  // Google new user onboarding
  if(googleNewUser){
    return(
      <div style={S.app}>
        <GoogleOnboarding user={googleNewUser}
          onComplete={async(name,cls)=>{
            // Apply referral bonus if ?ref= was in the URL
            const refCode=getReferralFromUrl();
            const referralBonus=refCode?100:0;
            const{data:newProfile}=await supabase.from('bm_child_profiles')
              .insert({parent_email:googleNewUser.email,child_name:name,
                class_level:cls,xp_points:referralBonus,streak_count:0,current_level:1})
              .select().single();
            if(newProfile){
              localStorage.setItem('ns_profile_'+googleNewUser.email,JSON.stringify(newProfile));
            }
            // Award XP to referrer + Lucky notification
            if(refCode){
              const referrerEmail=decodeReferralCode(refCode);
              if(referrerEmail&&referrerEmail!==googleNewUser.email){
                const{data:referrer}=await supabase.from('bm_child_profiles')
                  .select('xp_points,child_name,pet_name,pet_type').eq('parent_email',referrerEmail).single();
                if(referrer){
                  await supabase.from('bm_child_profiles')
                    .update({xp_points:(referrer.xp_points||0)+100})
                    .eq('parent_email',referrerEmail);
                  // Send Lucky push to referrer
                  fetch('/api/send-referral-push',{
                    method:'POST',
                    headers:{'Content-Type':'application/json'},
                    body:JSON.stringify({
                      referrerEmail,
                      referrerName:referrer.child_name,
                      petName:referrer.pet_name||'Lucky',
                      petType:referrer.pet_type||'lumie',
                      newFriendName:newProfile.child_name||'Someone',
                    }),
                  }).catch(()=>{});
                }
              }
            }
            setGoogleNewUser(null);
            setProfile(newProfile);setXp(referralBonus);
            if(referralBonus>0){fireConfetti();}
          }}/>
      </div>
    );
  }

  // Teacher portal
  if(showTeacherRegister) return(
    <TeacherPortalScreen
      onBack={()=>setShowTeacherRegister(false)}
      onSuccess={(t)=>{setTeacher(t);setShowTeacherRegister(false);}}/>
  );

  if(teacher) return(
    <TeacherDashboard teacher={teacher} onLogout={async()=>{
      await supabase.auth.signOut();
      setTeacher(null);setAuthChecked(true);
    }}/>
  );

  if(!user){
    if(showLanding) return(
      <LandingPage
        onGetStarted={()=>setShowLanding(false)}
        onTeacherLogin={()=>{setShowLanding(false);setShowTeacherRegister(true);}}/>
    );
    return(
      <div style={{minHeight:'100vh',background:'linear-gradient(135deg,#0A0A2E 0%,#1A1A5E 60%,#1A3A8F 100%)',
        display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Nunito',sans-serif",
        position:'relative',overflow:'hidden'}}>
        {/* dot grid */}
        <div style={{position:'absolute',inset:0,opacity:0.04,
          backgroundImage:'radial-gradient(#fff 1px,transparent 1px)',backgroundSize:'32px 32px'}}/>
        {/* left branding — hidden on small screens */}
        <div className="ns-auth-brand" style={{flex:1,maxWidth:480,padding:'48px',color:'white',display:'none'}}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:32}}>
            <span style={{fontSize:32}}>⭐</span>
            <span style={{fontSize:24,fontWeight:900}}>NeevStar</span>
          </div>
          <h2 style={{fontSize:36,fontWeight:900,lineHeight:1.2,margin:'0 0 20px'}}>
            School teaches the syllabus.<br/>
            <span style={{background:'linear-gradient(90deg,#FFD600,#FF9500)',WebkitBackgroundClip:'text',
              WebkitTextFillColor:'transparent',backgroundClip:'text'}}>We teach the future.</span>
          </h2>
          <p style={{fontSize:15,opacity:0.75,fontWeight:700,lineHeight:1.7,margin:'0 0 32px'}}>
            Entrepreneurship • Money • AI • Olympiad Prep • STEM • Brain Games — all in one app for Classes 3–8.
          </p>
          {['🏪 Biz World — run a virtual startup','💰 Money School — save, invest, grow',
            '🤖 AI School — learn how AI works','🏆 Olympiad Prep — IMO, NSO, IEO',
            '📖 CBSE Practice — chapter by chapter'].map(f=>(
            <div key={f} style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
              <span style={{fontSize:16}}>{f.split('—')[0].trim()}</span>
              <span style={{fontSize:13,opacity:0.7,fontWeight:700}}>— {f.split('—')[1].trim()}</span>
            </div>
          ))}
          <div style={{marginTop:32,display:'flex',gap:8,flexWrap:'wrap'}}>
            <span style={{background:'rgba(255,255,255,0.1)',borderRadius:20,padding:'6px 14px',fontSize:12,fontWeight:800}}>Free to start</span>
            <span style={{background:'rgba(255,255,255,0.1)',borderRadius:20,padding:'6px 14px',fontSize:12,fontWeight:800}}>Classes 3–8</span>
            <span style={{background:'rgba(255,255,255,0.1)',borderRadius:20,padding:'6px 14px',fontSize:12,fontWeight:800}}>Works on any phone</span>
          </div>
        </div>
        {/* auth card */}
        <div style={{width:'100%',maxWidth:420,position:'relative',zIndex:1,
          background:'white',borderRadius:24,overflow:'hidden',
          boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
          <AuthScreen onAuth={handleAuth} onTeacherPortal={()=>setShowTeacherRegister(true)} skipSplash={true}/>
        </div>
        <style>{`@media(min-width:900px){.ns-auth-card{background:white;border-radius:24px;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,0.3);}}`}</style>
        <style>{`
          .ns-auth-brand{display:none!important}
          @media(min-width:900px){
            .ns-auth-brand{display:block!important}
            .ns-auth-screen{min-height:unset!important;background:transparent!important;justify-content:center!important;}
            .ns-auth-splash{display:none!important}
            .ns-auth-cta{background:none!important;padding:0!important;display:flex;flex-direction:column;justify-content:center;min-height:100%;}
          }
        `}</style>
      </div>
    );
  }

  // Gate screen for Mock Test
  const MockTestGate=({onBack,done,onGoOlympiad})=>(
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
      minHeight:'70vh',padding:'40px 24px',textAlign:'center',fontFamily:"'Nunito',sans-serif"}}>
      <div style={{fontSize:64,marginBottom:16}}>🏟️</div>
      <h2 style={{fontSize:22,fontWeight:900,color:'#1A1A2E',margin:'0 0 8px'}}>Test Colosseum</h2>
      <p style={{fontSize:14,color:'#888',fontWeight:700,lineHeight:1.6,margin:'0 0 20px'}}>
        Complete <strong>5 Olympiad sessions</strong> first to unlock full Mock Tests.<br/>
        They prepare you with the right level of questions!
      </p>
      <div style={{background:'#F8F9FF',borderRadius:18,padding:'16px 20px',marginBottom:24,width:'100%',maxWidth:300}}>
        <p style={{margin:'0 0 8px',fontSize:13,fontWeight:900,color:'#888',letterSpacing:'0.04em'}}>YOUR PROGRESS</p>
        <div style={{display:'flex',justifyContent:'center',gap:8,marginBottom:8}}>
          {[1,2,3,4,5].map(n=>(
            <div key={n} style={{width:36,height:36,borderRadius:'50%',
              background:done>=n?'linear-gradient(135deg,#FF6B00,#FFD600)':'#E8E8E8',
              display:'flex',alignItems:'center',justifyContent:'center',
              fontSize:14,fontWeight:900,color:done>=n?'white':'#CCC'}}>
              {done>=n?'✓':n}
            </div>
          ))}
        </div>
        <p style={{margin:0,fontSize:12,fontWeight:700,color:done>=5?'#00C851':'#888'}}>
          {done>=5?'✅ Unlocked!': `${done}/5 sessions done`}
        </p>
      </div>
      <motion.button whileTap={{scale:0.97}} onClick={onGoOlympiad}
        style={{padding:'14px 32px',borderRadius:16,border:'none',
          background:'linear-gradient(135deg,#00695C,#1A1A6E)',color:'white',
          fontSize:15,fontWeight:900,cursor:'pointer',marginBottom:10,
          fontFamily:"'Nunito',sans-serif",boxShadow:'0 4px 16px rgba(255,107,0,0.35)'}}>
        🏆 Go to Olympiad Prep
      </motion.button>
      <button onClick={onBack} style={{background:'none',border:'none',color:'#AAA',
        fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:"'Nunito',sans-serif"}}>
        ← Back
      </button>
    </div>
  );

  // renderScreen moved to ScreenRouter.js

  const navItems=[
    {id:'home',icon:'🏠',label:'Home'},
    {id:'olympiad',icon:'🏆',label:'Olympiad'},
    {id:'quiz',icon:'🏆',label:'Quiz'},
    {id:'leaderboard',icon:'📊',label:'Ranks'},
    {id:'profile',icon:'👤',label:'Profile'},
  ];

  return(
    <ShareCardProvider profile={profile} coins={neevCoins}>
    <>
      <div className="ns-app-shell">
        <DesktopSidebar screen={screen} setScreen={setScreen} profile={profile} xp={xp} streak={streak} isPremium={isPremium} onLogout={handleLogout}/>
        <ToastProvider/>
        <div style={S.app} className="ns-app">
          <AnimatePresence mode="wait">
            <motion.div key={screen}
              initial={{opacity:0,x:30}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-30}}
              transition={{duration:0.2}}>
              <ScreenErrorBoundary key={screen}>
                <ScreenRouter
                  screen={screen} profile={profile} xp={xp} streak={streak}
                  neevCoins={neevCoins} completedCounts={completedCounts}
                  isPremium={isPremium} user={user}
                  setScreen={setScreen} addXP={addXP} addCoins={addCoins}
                  handleUpgrade={handleUpgrade} handleLogout={handleLogout}
                  handleGradeChange={handleGradeChange} handleDailyChallenge={handleDailyChallenge}
                  homeRefreshTick={homeRefreshTick}
                  openSpin={()=>setShowLuckySpin(true)}
                  openShop={()=>setShowShop(true)}
                  openChat={()=>setShowNeevBotChat(true)}
                  todayXPEarned={todayXPEarned}
                  HomeScreen={HomeScreen} ScenarioScreen={ScenarioScreen}
                  QuizWorld={QuizWorld} StoriesScreen={StoriesScreen}
                  ChallengesScreen={ChallengesScreen} NewsScreen={NewsScreen}
                  MoneySchoolHome={MoneySchoolHome} OlympiadScreen={OlympiadScreen}
                  MathsScreen={MathsScreen} ScienceStemWorld={ScienceStemWorld}
                  ScienceWorldHome={ScienceWorldHome} StemScreen={StemScreen}
                  AITutorScreen={AITutorScreen} MockTestScreen={MockTestScreen}
                  MockTestGate={MockTestGate} LeaderboardScreen={LeaderboardScreen}
                  CertificateScreen={CertificateScreen} LanguageScreen={LanguageScreen}
                  GamesScreen={GamesScreen} CreatorStudio={CreatorStudio}
                  MultiplayerQuiz={MultiplayerQuiz} NeevPetWorld={NeevPetWorld}
                  CardCollection={CardCollection} AchievementFeedFull={AchievementFeedFull}
                  HomeworkHelper={HomeworkHelper} SubjectPracticeScreen={SubjectPracticeScreen}
                  StudentProgressScreen={StudentProgressScreen} ProfileScreen={ProfileScreen}
                  ParentReportScreen={ParentReportScreen}
                />
              </ScreenErrorBoundary>
            </motion.div>
          </AnimatePresence>
          {/* ── Always-present pet companion — every screen except pet world ── */}
          {profile && screen !== 'neevpet' && (
            <PetCompanion
              screen={screen} xp={xp} streak={streak}
              profile={profile} onNavigate={setScreen}
            />
          )}
          <div style={S.bottomNav} className="ns-bottom-nav">
            {navItems.map(nav=>(
              <button key={nav.id} style={{...S.navItem,color:screen===nav.id?T.orange:'#999'}}
                onClick={()=>nav.id==='logout'?handleLogout():setScreen(nav.id)}>
                <motion.span style={{fontSize:'24px'}} animate={screen===nav.id?{scale:[1,1.3,1]}:{scale:1}}
                  transition={{duration:0.3}}>
                  {nav.icon}
                </motion.span>
                <span style={{fontSize:'10px',fontWeight:'800',color:screen===nav.id?T.orange:'#BBB'}}>
                  {nav.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
      {showOnboarding&&(
        <OnboardingModal
          profile={profile}
          onXPEarned={addXP}
          onNavigate={setScreen}
          onDone={(pickedSubjects)=>{
            setShowOnboarding(false);
            if(user?.email){
              localStorage.setItem('ns_onboarded_'+user.email,'1');
              // Save subject preferences for home screen ordering
              if(pickedSubjects?.length>0){
                localStorage.setItem('ns_fav_subjects_'+user.email,JSON.stringify(pickedSubjects));
                supabase.from('bm_child_profiles')
                  .update({favorite_subjects:pickedSubjects})
                  .eq('parent_email',user.email)
                  .then(()=>{}).catch(()=>{});
              }
            }
          }}
        />
      )}

      {/* Pet picker for existing users who never picked */}
      {showPetPicker&&(
        <ExistingUserPetPicker
          profile={profile}
          onDone={()=>setShowPetPicker(false)}
        />
      )}

      {/* Push notification permission prompt */}
      {showPushPrompt&&(
        <PushPermissionPrompt
          profile={profile}
          user={user}
          onDismiss={dismissPushPrompt}
        />
      )}

      {/* Founding Explorer Badge */}
      {showFoundingBadge&&foundingNumber&&(
        <FoundingBadge
          number={foundingNumber}
          childName={profile?.child_name}
          onClose={()=>setShowFoundingBadge(false)}
        />
      )}

      {/* NeevBot Chat */}
      <AnimatePresence>
        {showNeevBotChat&&(
          <NeevBotChat
            profile={profile}
            xp={xp}
            streak={streak||0}
            coins={neevCoins||0}
            completedCounts={completedCounts}
            onClose={()=>setShowNeevBotChat(false)}
          />
        )}
      </AnimatePresence>

      {showLuckySpin&&(
        <LuckySpin
          profile={profile}
          onCoinsEarned={addCoins}
          onXPEarned={addXP}
          onClose={()=>setShowLuckySpin(false)}
        />
      )}

      {/* NeevCoins Shop */}
      {showShop&&(
        <NeevCoinsShop
          coins={neevCoins}
          profile={profile}
          onSpendCoins={spendCoins}
          onOpenSpin={()=>{setShowShop(false);setShowLuckySpin(true);}}
          onClose={()=>setShowShop(false)}
        />
      )}

      {/* Daily Login Reward */}
      {showDailyReward&&(
        <DailyReward
          streak={streak||1}
          onClaim={(coins)=>{addCoins(coins);}}
          onClose={()=>setShowDailyReward(false)}
        />
      )}

      {/* Streak Rescue Modal */}
      {streakRescue&&(
        <StreakRescueModal
          brokenStreak={streakRescue.brokenStreak}
          onAccept={handleStreakRescue}
          onDecline={()=>setStreakRescue(null)}
        />
      )}

      {/* Streak Milestone Modal */}
      {streakMilestone&&(
        <StreakChest
          streak={streakMilestone}
          onCoinsEarned={(c)=>{
            addCoins(c);
            const bonus=streakMilestone>=30?100:streakMilestone>=14?50:streakMilestone>=7?30:15;
            addXP(bonus);
            if(streakMilestone>=30&&profile?.parent_email) addBox(profile.parent_email,'legendary');
            // Share card on streak chest
            const chestNames={3:'Curious Explorer Chest',7:'Future Innovator Chest',14:'NeevStar Pioneer Chest',30:'City Builder Legend Chest'};
            const chestName=chestNames[streakMilestone]||'Streak Chest';
            setTimeout(()=>showShareCard('chest',chestName,profile?.child_name,c),600);
            postActivity({userId:user?.id,childName:profile?.child_name,neevbotLevel:profile?.current_level,activityType:'chest',activityDetail:`unlocked the ${chestName}! 📦`,coinsEarned:c});
          }}
          onClose={()=>setStreakMilestone(null)}
        />
      )}

      {/* PWA Install Banner */}
      {showPwaBanner&&(
        <motion.div initial={{y:100,opacity:0}} animate={{y:0,opacity:1}} exit={{y:100,opacity:0}}
          style={{position:'fixed',bottom:80,left:0,right:0,zIndex:9990,padding:'0 16px'}}>
          <div style={{background:'linear-gradient(135deg,#1A1A2E,#16213E)',borderRadius:20,padding:'14px 16px',
            display:'flex',alignItems:'center',gap:12,boxShadow:'0 8px 32px rgba(0,0,0,0.3)'}}>
            <span style={{fontSize:28}}>📱</span>
            <div style={{flex:1}}>
              <p style={{fontSize:13,fontWeight:900,color:'white',margin:'0 0 2px'}}>🎉 You've earned 50 XP! Add to Home Screen</p>
              <p style={{fontSize:11,color:'rgba(255,255,255,0.7)',margin:0,fontWeight:700}}>One tap to install • Works like a real app!</p>
            </div>
            <button onClick={async()=>{
              if(pwaPrompt){await pwaPrompt.prompt();setShowPwaBanner(false);localStorage.setItem('ns_pwa_dismissed','1');}
            }} style={{background:'linear-gradient(135deg,#FFD600,#FF9500)',border:'none',borderRadius:12,padding:'8px 14px',
              color:'white',fontSize:12,fontWeight:900,cursor:'pointer',fontFamily:"'Nunito',sans-serif"}}>
              Install
            </button>
            <button onClick={()=>{setShowPwaBanner(false);localStorage.setItem('ns_pwa_dismissed','1');}}
              style={{background:'none',border:'none',color:'rgba(255,255,255,0.5)',fontSize:18,cursor:'pointer',padding:'4px'}}>
              ✕
            </button>
          </div>
        </motion.div>
      )}
    </>
    </ShareCardProvider>
  );
}
